export type TraceToolErrorKind = 'short' | 'markdown' | 'json' | 'long';

export type TraceToolErrorDisplay = {
  summary?: string;
  metadataSummary?: string;
  detail?: string;
  kind: TraceToolErrorKind;
};

const SUMMARY_MAX_LENGTH = 120;
const SHORT_ERROR_MAX_LENGTH = 500;
const JSON_MESSAGE_KEYS = ['message', 'error', 'detail', 'title'] as const;

function readTrimmedString(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();

  return trimmed.length > 0 ? trimmed : undefined;
}

function looksLikeMarkdown(content: string): boolean {
  return (
    content.includes('##') ||
    content.includes('**') ||
    content.includes('```') ||
    /^[-*]\s/m.test(content) ||
    /^\d+\.\s/m.test(content)
  );
}

function parseJsonRecord(content: string): unknown {
  if (!content.startsWith('{') && !content.startsWith('[')) {
    return undefined;
  }

  try {
    return JSON.parse(content);
  } catch {
    return undefined;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function extractJsonMessage(parsed: unknown): string | undefined {
  if (!isRecord(parsed)) {
    return undefined;
  }

  return JSON_MESSAGE_KEYS.map((key) => readTrimmedString(parsed[key])).find(
    (message) => message !== undefined,
  );
}

function firstLine(content: string): string {
  const line = content.split('\n').find((candidate) => candidate.trim());

  return (line ?? '')
    .replace(/^[\s#>*`-]+/, '')
    .replace(/\*\*/g, '')
    .trim();
}

function capSummary(value: string): string {
  return value.length > SUMMARY_MAX_LENGTH
    ? `${value.slice(0, SUMMARY_MAX_LENGTH)}…`
    : value;
}

export function resolveTraceToolErrorDisplay({
  error,
  metadata,
}: {
  error?: string;
  metadata?: Record<string, unknown>;
}): TraceToolErrorDisplay {
  const rawDetail = readTrimmedString(error);
  const metadataSummary =
    readTrimmedString(metadata?.['debugMessage']) ??
    readTrimmedString(metadata?.['errorLabel']);
  const parsedJson = rawDetail ? parseJsonRecord(rawDetail) : undefined;

  if (rawDetail && parsedJson !== undefined) {
    const jsonMessage = extractJsonMessage(parsedJson);

    return {
      summary: capSummary(
        metadataSummary ?? jsonMessage ?? firstLine(rawDetail),
      ),
      metadataSummary: metadataSummary && capSummary(metadataSummary),
      detail: JSON.stringify(parsedJson, null, 2),
      kind: 'json',
    };
  }

  const kind: TraceToolErrorKind = !rawDetail
    ? 'short'
    : looksLikeMarkdown(rawDetail)
      ? 'markdown'
      : rawDetail.length < SHORT_ERROR_MAX_LENGTH
        ? 'short'
        : 'long';
  const summarySource =
    metadataSummary ?? (rawDetail ? firstLine(rawDetail) : undefined);

  return {
    summary: summarySource ? capSummary(summarySource) : undefined,
    metadataSummary: metadataSummary && capSummary(metadataSummary),
    detail: rawDetail,
    kind,
  };
}
